import { z } from "zod";
import { API_BASE_URL } from "../app/config";

const trackSchema = z.object({
  id: z.string(),
  name: z.string(),
  artist: z.string(),
  duration_ms: z.number(),
  album_id: z.string()
});

const buyLinkSchema = z.object({
  vendor: z.string(),
  format: z.string(),
  url: z.string()
});

const priceInfoSchema = z.object({
  amount: z.union([z.string(), z.number()]).transform((v) => Number(v)),
  source: z.string() // "discogs" or "estimated"
});

const albumPricesSchema = z.object({
  cd: priceInfoSchema.nullable(),
  vinyl: priceInfoSchema.nullable()
});

const albumSchema = z.object({
  id: z.string(),
  name: z.string(),
  artist: z.string(),
  cover_url: z.string().url(),
  url: z.string().url(),
  release_year: z.number().nullable(),
  track_count: z.number(),
  tracks: z.array(trackSchema),
  prices: albumPricesSchema,
  buy_links: z.array(buyLinkSchema).optional().default([])
});

const totalsSchema = z.object({
  cd: z.union([z.string(), z.number()]).transform((v) => Number(v)),
  vinyl: z.union([z.string(), z.number()]).transform((v) => Number(v))
});

const playlistResponseSchema = z.object({
  playlist_id: z.string(),
  playlist_name: z.string(),
  track_count: z.number(),
  album_count: z.number(),
  albums: z.array(albumSchema),
  totals: totalsSchema,
  currency: z.string()
});

export type PlaylistApiResponse = z.infer<typeof playlistResponseSchema>;
export type Album = z.infer<typeof albumSchema>;
export type Track = z.infer<typeof trackSchema>;
export type BuyLink = z.infer<typeof buyLinkSchema>;
export type PriceInfo = z.infer<typeof priceInfoSchema>;
export type AlbumPrices = z.infer<typeof albumPricesSchema>;

type RecommendationsRequest = {
  playlistRef: string;
  turnstileToken?: string;
};

const buildUrl = (): string => {
  const normalized = API_BASE_URL.endsWith("/")
    ? API_BASE_URL.slice(0, -1)
    : API_BASE_URL;
  return `${normalized}/api/playlists/recommendations`;
};

const toErrorMessage = async (response: Response): Promise<string> => {
  try {
    const data = await response.json();
    if (typeof data?.detail === "string") {
      return data.detail;
    }
    if (Array.isArray(data?.detail) && data.detail[0]?.msg) {
      return data.detail[0].msg as string;
    }
  } catch {
    // Ignore parse errors and fall back to status text
  }
  return response.statusText || "Unable to fetch recommendations";
};

export const requestPlaylistRecommendations = async ({
  playlistRef,
  turnstileToken
}: RecommendationsRequest): Promise<PlaylistApiResponse> => {
  const response = await fetch(buildUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(turnstileToken ? { "CF-Turnstile-Token": turnstileToken } : {})
    },
    body: JSON.stringify({ playlist_url: playlistRef })
  });

  if (!response.ok) {
    const message = await toErrorMessage(response);
    throw new Error(message);
  }

  const payload = await response.json();
  return playlistResponseSchema.parse(payload);
};
